// AAM Website - Main JavaScript

// Import styles for Vite bundling
import '../scss/main.scss';
// Initialize all functionalities on DOMContentLoaded
import './SiteWrapper.js';
import './DefinitionTooltip.js';
import './HoverDef.js';
import './TooltipTemplates.js';
import './StarField.js';
import './mathjax-config.js';
import './SubnavController.js';

document.addEventListener('DOMContentLoaded', () => {
  initAccordions();
  initDefinitions();
  initMobileMenu();
  initSidebarToggle();
  initTableOfContents();
  initSidebarHierarchy();
});

// Accordion functionality
function initAccordions() {
  const accordionHeaders = document.querySelectorAll('.accordion-header');

  accordionHeaders.forEach(header => {
    header.addEventListener('click', () => {
      const content = header.nextElementSibling;
      const isOpen = content.classList.contains('open');

      // Close all accordions in the same accordion container
      const accordion = header.closest('.accordion');
      if (accordion) {
        accordion.querySelectorAll('.accordion-content').forEach(c => {
          c.classList.remove('open');
        });
        accordion.querySelectorAll('.accordion-header').forEach(h => {
          h.classList.remove('open');
        });
      }

      // Open clicked accordion if it was closed
      if (!isOpen) {
        content.classList.add('open');
        header.classList.add('open');
      }
    });
  });
}

// Definition tooltips
function initDefinitions() {
  const definitionTerms = document.querySelectorAll('.definition-term');
  let tooltip = null;

  definitionTerms.forEach(term => {
    term.addEventListener('mouseenter', async () => {
      const slug = term.dataset.definition;
      if (!slug) return;

      // Create tooltip if it doesn't exist
      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.className = 'definition-tooltip';
        document.body.appendChild(tooltip);
      }

      // Fetch definition content
      try {
        const response = await fetch(`/definition/${slug}/`);
        if (response.ok) {
          tooltip.innerHTML = await response.text();
          positionTooltip(tooltip, term);
          tooltip.classList.add('visible');
        }
      } catch (err) {
        console.error('Failed to load definition:', err);
      }
    });

    term.addEventListener('mouseleave', () => {
      if (tooltip) {
        tooltip.classList.remove('visible');
      }
    });
  });
}

function positionTooltip(tooltip, target) {
  const rect = target.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

  // Position below the term
  let left = rect.left + scrollLeft;
  let top = rect.bottom + scrollTop + 8;

  // Adjust if tooltip would go off screen
  const tooltipRect = tooltip.getBoundingClientRect();
  if (left + tooltipRect.width > window.innerWidth) {
    left = window.innerWidth - tooltipRect.width - 16;
  }

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
}

// Mobile menu toggle
function initMobileMenu() {
  const navToggle = document.querySelector('.nav-toggle');
  const navMenu = document.querySelector('.nav-menu');

  if (navToggle && navMenu) {
    navToggle.addEventListener('click', () => {
      const isOpen = navMenu.classList.contains('open');
      navMenu.classList.toggle('open');
      navToggle.setAttribute('aria-expanded', !isOpen);
    });

    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!navToggle.contains(e.target) && !navMenu.contains(e.target)) {
        navMenu.classList.remove('open');
        navToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }
}

// Sidebar toggle for mobile
function initSidebarToggle() {
  const siteBody = document.querySelector('.site-body');
  const sidebar = document.querySelector('.site-sidebar');

  // Create sidebar toggle button if sidebar exists
  if (sidebar && siteBody) {
    // Check if we're on a page with sidebar (not home)
    const navToggle = document.querySelector('.nav-toggle');

    if (navToggle) {
      // Add sidebar toggle functionality to the nav toggle on mobile
      navToggle.addEventListener('click', () => {
        if (window.innerWidth <= 1024) {
          siteBody.classList.toggle('sidebar-open');
        }
      });
    }

    // Close sidebar when clicking outside on mobile
    document.addEventListener('click', (e) => {
      if (window.innerWidth <= 1024) {
        if (!sidebar.contains(e.target) && !e.target.closest('.nav-toggle')) {
          siteBody.classList.remove('sidebar-open');
        }
      }
    });

    // Close sidebar when window is resized to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth > 1024) {
        siteBody.classList.remove('sidebar-open');
      }
    });
  }
}

// Table of Contents scroll highlighting
function initTableOfContents() {
  const tocLinks = document.querySelectorAll('.toc-list a');
  if (tocLinks.length === 0) return;

  // Get all section headings that TOC links point to
  const headings = [];
  tocLinks.forEach(link => {
    const id = link.getAttribute('href')?.slice(1);
    if (id) {
      const heading = document.getElementById(id);
      if (heading) {
        headings.push({ id, element: heading, link });
      }
    }
  });

  if (headings.length === 0) return;

  // Highlight TOC item based on scroll position
  function updateActiveLink() {
    const scrollTop = window.scrollY;
    const offset = 100; // Offset from top to trigger highlight

    let activeHeading = headings[0];

    for (const heading of headings) {
      const rect = heading.element.getBoundingClientRect();
      const elementTop = rect.top + scrollTop;

      if (scrollTop >= elementTop - offset) {
        activeHeading = heading;
      }
    }

    // Update active class
    tocLinks.forEach(link => link.classList.remove('active'));
    if (activeHeading) {
      activeHeading.link.classList.add('active');
    }
  }

  // Throttle scroll events
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        updateActiveLink();
        ticking = false;
      });
      ticking = true;
    }
  });

  // Initial highlight
  updateActiveLink();

  // Smooth scroll to section when clicking TOC link (only for anchor links)
  tocLinks.forEach(link => {
    const href = link.getAttribute('href');
    // Only add smooth scroll for anchor links (starting with #), not page navigation links
    if (href && href.startsWith('#')) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const id = href.slice(1);
        const target = document.getElementById(id);
        if (target) {
          const navHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--layout-nav-height')) || 64;
          const targetPosition = target.getBoundingClientRect().top + window.scrollY - navHeight - 20;
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    }
  });
}

// Sidebar hierarchy expand/collapse
function initSidebarHierarchy() {
  const sidebar = document.querySelector('.sidebar-hierarchy');
  if (!sidebar) return;

  // Find all clickable category/subcategory labels
  const toggles = sidebar.querySelectorAll('.sidebar-category, .sidebar-subcategory');

  toggles.forEach(toggle => {
    // Only add click handler if parent has children
    const parentLi = toggle.closest('.has-children');
    if (!parentLi) return;

    // Make it visually clickable
    toggle.style.cursor = 'pointer';

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      parentLi.classList.toggle('expanded');
    });
  });
}
